<template>
  <div class="success-page">
    <div class="result-card" v-if="classInfo">
      <div class="title">
        <span class="title-icon">🎉</span>
        <span>分班结果</span>
      </div>

      <div class="class-display">
        <div class="class-hint">你的班级是</div>
        <div class="class-name">{{ classInfo.grade }} 年（{{ classInfo.class }}）班</div>
        <div class="class-teacher" v-if="classInfo.teacher">班主任：{{ classInfo.teacher }}</div>
      </div>

      <div class="qr-section" v-if="qrCodeSrc">
        <div class="qr-caption">扫码加入班级群</div>
        <div class="qr-frame">
          <img :src="qrCodeSrc" alt="班级二维码" class="qr-code" />
        </div>
        <div class="qr-warning" v-if="classInfo.watermarkEnabled">请不要转发班级群二维码</div>
      </div>

      <el-descriptions :column="1" border class="student-info" title="学生信息">
        <el-descriptions-item label="姓名">{{ studentName }}</el-descriptions-item>
        <el-descriptions-item label="预报名号">{{ classInfo.preregno }}</el-descriptions-item>
        <el-descriptions-item label="座位号">{{ classInfo.sitNumber }}</el-descriptions-item>
        <el-descriptions-item label="班主任">{{ classInfo.teacher }}</el-descriptions-item>
      </el-descriptions>

      <div class="campus-section" v-if="campusPlans.length">
        <div class="campus-caption">学校平面图</div>
        <div class="campus-list">
          <div class="campus-item" v-for="plan in campusPlans" :key="plan.name">
            <img :src="plan.url" :alt="plan.displayName" class="campus-img" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { ElLoading } from 'element-plus';
import axios from 'axios';

export default {
  name: 'SuccessPage',
  setup() {
    const studentName = ref('');
    const classInfo = ref(null);
    const qrCodeSrc = ref(null);
    const campusPlans = ref([]);
    const router = useRouter();

    const fetchCampusPlans = async () => {
      try {
        const { data } = await axios.get('/api/campus-plans');
        campusPlans.value = data.plans || [];
      } catch (error) {
        console.error('平面图加载失败:', error);
      }
    };

    onMounted(async () => {
      const name = localStorage.getItem('admittedStudentName');
      const studentId = localStorage.getItem('admittedStudentId');
      const registrationNumber = localStorage.getItem('admittedStudentPreregno');
      localStorage.removeItem('admittedStudentName');
      localStorage.removeItem('admittedStudentId');
      localStorage.removeItem('admittedStudentPreregno');

      if (!name || !studentId || !registrationNumber) {
        router.push('/');
        return;
      }

      studentName.value = name;
      const loadingInstance = ElLoading.service({
        lock: true,
        text: '信息正在加载中...',
        background: 'rgba(0, 0, 0, 0.7)',
      });

      try {
        const { data } = await axios.post('/get-student-info', {
          studentName: name,
          studentId,
          registrationNumber,
        });
        if (data?.classInfo) {
          classInfo.value = data.classInfo;
          qrCodeSrc.value = data.classInfo.qrCodeSrc;
        }
        await fetchCampusPlans();
      } catch (error) {
        console.error('信息加载失败:', error);
        router.push('/');
      } finally {
        loadingInstance.close();
      }
    });

    return {
      studentName,
      classInfo,
      qrCodeSrc,
      campusPlans,
    };
  },
};
</script>

<style scoped>
.success-page {
  min-height: 100vh;
  padding: 24px 16px;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  background: linear-gradient(180deg, #eef4ff 0%, #f7f9fc 100%);
}

.result-card {
  width: 100%;
  max-width: 480px;
  padding: 28px 24px 32px;
  background-color: #ffffff;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(3, 130, 230, 0.12);
}

.title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 22px;
  font-size: 24px;
  font-weight: 700;
  color: #1f2d3d;
}

.title-icon {
  font-size: 26px;
}

.class-display {
  background: linear-gradient(135deg, #0396ff 0%, #0382e6 100%);
  color: #ffffff;
  text-align: center;
  padding: 26px 16px;
  border-radius: 14px;
  margin-bottom: 24px;
  box-shadow: 0 6px 16px rgba(3, 130, 230, 0.35);
}

.class-hint {
  font-size: 14px;
  opacity: 0.9;
  letter-spacing: 2px;
}

.class-name {
  margin-top: 8px;
  font-size: 30px;
  font-weight: 700;
  letter-spacing: 1px;
}

.class-teacher {
  margin-top: 10px;
  font-size: 14px;
  opacity: 0.92;
}

.qr-section {
  text-align: center;
  margin-bottom: 26px;
}

.qr-caption {
  font-size: 14px;
  color: #606266;
  margin-bottom: 12px;
}

.qr-frame {
  display: inline-block;
  padding: 12px;
  background-color: #ffffff;
  border: 1px solid #ebeef5;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.qr-code {
  display: block;
  width: 200px;
  max-width: 60vw;
  height: auto;
  border-radius: 6px;
}

.qr-warning {
  margin-top: 10px;
  color: #e53935;
  font-size: 14px;
  font-weight: 700;
}

.student-info {
  margin-top: 8px;
}

.student-info :deep(.el-descriptions__label) {
  width: 96px;
  font-weight: 600;
  color: #303133;
}

.campus-section {
  margin-top: 26px;
}

.campus-caption {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  text-align: center;
  margin-bottom: 14px;
}

.campus-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.campus-item {
  padding: 8px;
  background-color: #ffffff;
  border: 1px solid #ebeef5;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.campus-img {
  display: block;
  width: 100%;
  height: auto;
  border-radius: 6px;
}
</style>
